/**
 * Profile API — routes from `C:\\Users\\donny\\testings\\profile\\*.js` (same host/auth as Assets/Bookings).
 */
import { requestAssetApi, type RequestAssetApiDebugSnapshot } from './getAssetById';
import {
  buildWorkerAvailableCreditsPatchBody,
  buildWorkerProfileImagePatchBody,
} from '../utils/businessClientWorkerCredits';
import { buildPrivateClientCreditsTopUpBody } from '../utils/privateClientCredits';
import { compressProfileAvatarDataUrlForUpload } from '../utils/profileAvatarUpload';
import { sanitizeProfilePatchBody } from '../utils/profilePrivateClientPatch';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export type ProfileApiCallOptions = {
  onRequestDebug?: (snapshot: RequestAssetApiDebugSnapshot) => void;
};

export async function fetchProfile(opts?: ProfileApiCallOptions): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/profile', onRequestDebug: opts?.onRequestDebug });
}

export async function fetchWorkersProfiles(opts?: ProfileApiCallOptions): Promise<unknown> {
  return requestAssetApi({ method: 'GET', path: '/profile/workers', onRequestDebug: opts?.onRequestDebug });
}

export async function patchProfile(body: unknown, opts?: ProfileApiCallOptions): Promise<unknown> {
  const prepared = isPlainObject(body) ? sanitizeProfilePatchBody(body) : body;
  return requestAssetApi({ method: 'PATCH', path: '/profile', body: prepared, onRequestDebug: opts?.onRequestDebug });
}

export async function patchUpdateWorker(body: unknown, opts?: ProfileApiCallOptions): Promise<unknown> {
  return requestAssetApi({ method: 'PATCH', path: '/profile/update-worker', body, onRequestDebug: opts?.onRequestDebug });
}

export async function patchWorkerAvailableCredits(
  workerId: string,
  availableCredits: number,
  opts?: ProfileApiCallOptions,
): Promise<unknown> {
  return patchUpdateWorker(buildWorkerAvailableCreditsPatchBody(workerId, availableCredits), opts);
}

/** Private client wallet top-up — call after a successful purchase (`POST /profile/credits/top-up`). */
export async function postProfileCreditsTopUp(
  credits: number,
  opts?: ProfileApiCallOptions,
): Promise<unknown> {
  return requestAssetApi({
    method: 'POST',
    path: '/profile/credits/top-up',
    body: buildPrivateClientCreditsTopUpBody(credits),
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function patchWorkerProfileImage(
  workerId: string,
  profileImageDataUrl: string,
  opts?: ProfileApiCallOptions,
): Promise<unknown> {
  const profileImage = await compressProfileAvatarDataUrlForUpload(profileImageDataUrl);
  return patchUpdateWorker(buildWorkerProfileImagePatchBody(workerId, profileImage), opts);
}

export async function postChangePassword(
  body: { old_password: string; new_password: string },
  opts?: ProfileApiCallOptions,
): Promise<unknown> {
  return requestAssetApi({
    method: 'POST',
    path: '/profile/change-password',
    body,
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function postProfileDeactivate(opts?: ProfileApiCallOptions): Promise<unknown> {
  return requestAssetApi({
    method: 'POST',
    path: '/profile/deactivate',
    body: {},
    onRequestDebug: opts?.onRequestDebug,
  });
}

export async function postDelistWorker(workerId: string, opts?: ProfileApiCallOptions): Promise<unknown> {
  const id = workerId.trim();
  return requestAssetApi({
    method: 'POST',
    path: '/profile/delist-worker',
    body: { worker_id: id },
    onRequestDebug: opts?.onRequestDebug,
  });
}

/** Mirror of `testings/profile/delistWorker.js` — sets worker status back to active. */
export async function postReactivateWorker(workerId: string, opts?: ProfileApiCallOptions): Promise<unknown> {
  const id = workerId.trim();
  return requestAssetApi({
    method: 'POST',
    path: '/profile/reactivate-worker',
    body: { worker_id: id },
    onRequestDebug: opts?.onRequestDebug,
  });
}
